import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Ban, Plus, Trash2, Loader2, ShieldX } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/misc';
import { PhoneInput } from '@/components/PhoneInput';
import { apiFetch } from '@/lib/api';
import { formatPhone } from '@/lib/phone';
import { useT } from '@/lib/i18n';
import type { Suppression } from '@/lib/contacts';

function relTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function Suppressions() {
  const t = useT();
  // Motivos que o sistema grava viram frase; o texto livre de quem bloqueou à
  // mão aparece como foi escrito.
  const reasonLabel = (r?: string) => {
    if (!r) return '—';
    const key = `suppressions.reasons.${r}`;
    const label = t(key);
    return label.endsWith(key) ? r : label;
  };
  const qc = useQueryClient();
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['suppressions'],
    queryFn: async (): Promise<Suppression[]> =>
      ((await apiFetch('GET', '/suppressions?limit=500')).data as { data?: Suppression[] })?.data ?? [],
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['suppressions'] });

  const add = async () => {
    if (!phone.trim()) { toast.error(t('suppressions.phoneRequired')); return; }
    setSaving(true);
    const r = await apiFetch('POST', '/suppressions', { phone: phone.trim(), reason: reason.trim() || undefined });
    setSaving(false);
    if (!r.ok) { toast.error(t('suppressions.addFailed')); return; }
    setPhone(''); setReason('');
    toast.success(t('suppressions.added'));
    refresh();
  };

  const remove = async (p: string) => {
    const r = await apiFetch('DELETE', `/suppressions?phone=${encodeURIComponent(p)}`);
    if (!r.ok) { toast.error(t('suppressions.removeFailed')); return; }
    toast.success(t('suppressions.removed'));
    refresh();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-2">
        <ShieldX className="mt-0.5 size-4 shrink-0 text-destructive" />
        <p className="text-sm text-muted-foreground">{t('suppressions.subtitle')}</p>
      </div>

      {/* Adicionar */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-[14rem] flex-col gap-1.5">
              <Label>{t('suppressions.phone')}</Label>
              <PhoneInput value={phone} onChange={setPhone} />
            </div>
            <div className="flex min-w-[12rem] flex-1 flex-col gap-1.5">
              <Label>{t('suppressions.reason')}</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('suppressions.reasonPlaceholder')} />
            </div>
            <Button onClick={add} disabled={saving || !phone.trim()}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t('suppressions.add')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          {isLoading ? (
            <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-14 text-center">
              <Ban className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('suppressions.empty')}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-3 font-medium">{t('suppressions.phone')}</th>
                  <th className="hidden px-5 py-3 font-medium sm:table-cell">{t('suppressions.reason')}</th>
                  <th className="hidden px-5 py-3 font-medium md:table-cell">{t('suppressions.since')}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b last:border-0">
                    <td className="px-5 py-3 font-medium">{formatPhone(s.phone)}</td>
                    <td className="hidden px-5 py-3 text-muted-foreground sm:table-cell">{reasonLabel(s.reason)}</td>
                    <td className="hidden px-5 py-3 text-xs text-muted-foreground md:table-cell">{relTime(s.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => remove(s.phone)}>
                        <Trash2 className="size-4" /> {t('suppressions.remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
